import React, { useEffect, useMemo, useState } from "react";
import "./TransformBuilder.css";
import { buildTransformCode } from "../utils/transform_codegen";

/* ---------- Helpers ---------- */

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

function deriveAlias(arrayPath) {
  if (!arrayPath) return "item";
  const last = arrayPath.split(".").pop() || "item";
  return last.replace(/\[\d+\]$/, "").replace(/^\$?/, "");
}

/**
 * Parse the text the user typed into a typed JS value:
 * - If user typed "1" or 'abc' (with quotes) -> string (quotes removed)
 * - If user typed 1 -> number
 * - true/false/null -> boolean/null
 * - $document... -> left as a path string
 * - otherwise -> string
 */
function parseValue(s) {
  let v = (s ?? "").trim();
  if (v === "") return "";

  // null / boolean
  if (/^null$/i.test(v)) return null;
  if (/^(true|false)$/i.test(v)) return /^true$/i.test(v);

  // JSONPath — keep verbatim if starts with $document
  if (/^\$document(\.|$)/.test(v)) return v;

  // quoted strings -> explicit string
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    return v.substring(1, v.length - 1);
  }

  // numbers without quotes -> Number
  if (/^-?\d+(\.\d+)?$/.test(v)) {
    return Number(v);
  }

  // fallback -> string
  return v;
}

function stringifyValue(v) {
  if (v === null) return "null";
  if (typeof v === "boolean" || typeof v === "number") return String(v);
  return v ?? "";
}

/* ---------- Defaults / templates (for reset) ---------- */

const defaultLanguage = "velocity";
const defaultConditionTemplate = () => ({
  id: uid(),
  type: "equals",
  left: "",
  right: "",
  rightRaw: "",
  rightIsPath: false,
});
const defaultActionTemplate = () => ({
  op: "add",
  path: "$document",
  newKey: "",
  value: "",
});

/* ---------- Component ---------- */

const TransformBuilder = ({ flattenedPaths = [] }) => {
  /* ---------- Derive selectable paths ---------- */
  const keyPaths = useMemo(() => {
    const set = new Set();
    for (const line of flattenedPaths) {
      const p = line.split(":")[0];
      if (p) set.add(p);
    }
    // include root for convenience
    set.add("$document");
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [flattenedPaths]);

  const arrayPaths = useMemo(() => {
    const set = new Set();
    for (const p of keyPaths) {
      const m = p.match(/^(.*?\[\d+\])/);
      if (m) {
        const root = m[1].replace(/\[\d+\]$/, "");
        if (root) set.add(root);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [keyPaths]);

  /* ---------- Local state (rule builder) ---------- */
  const [language, setLanguage] = useState(defaultLanguage); // "velocity" | "python"

  // Optional loop
  const [useLoop, setUseLoop] = useState(false);
  const [loopPath, setLoopPath] = useState(arrayPaths[0] || "");
  const [loopAlias, setLoopAlias] = useState(() => deriveAlias(arrayPaths[0] || ""));

  // Conditions (start empty — user must pick left/right)
  const [conditions, setConditions] = useState([defaultConditionTemplate()]);

  // Action
  const [action, setAction] = useState(defaultActionTemplate());

  // Generated code
  const [code, setCode] = useState("");

  /* ---------- Reset function (clears everything) ---------- */
  const resetBuilder = () => {
    setLanguage(defaultLanguage);
    setUseLoop(false);
    setLoopPath(arrayPaths[0] || "");
    setLoopAlias(deriveAlias(arrayPaths[0] || ""));
    setConditions([defaultConditionTemplate()]);
    setAction(defaultActionTemplate());
    setCode("");
  };

  /* ---------- Normalize + generate code effect ---------- */
  useEffect(() => {
    const normalized = normalizeRule({
      language,
      conditions,
      loop: useLoop && loopPath ? { path: loopPath, as: `$${loopAlias}` } : undefined,
      action,
    });

    // If there are no valid conditions and the action is incomplete -> show hint
    const noValidConditions = (normalized.conditions || []).length === 0;

    const actionOp = normalized.action?.op || "add";
    const actionIncomplete =
      (actionOp === "add" && (!normalized.action.newKey || normalized.action.newKey.trim() === "")) ||
      (actionOp === "update" && (!normalized.action.path || !normalized.action.value)) ||
      (actionOp === "remove" && (!normalized.action.path || normalized.action.path.trim() === ""));

    if (noValidConditions && actionIncomplete) {
      setCode(
        "// No rule configured.\n" +
        "// Add a condition or a valid action on the left to generate code."
      );
      return;
    }

    const output = buildTransformCode(normalized);
    setCode(output || "");
  }, [language, conditions, useLoop, loopPath, loopAlias, action]);

  useEffect(() => {
    if (!useLoop) return;
    setLoopAlias(deriveAlias(loopPath));
  }, [useLoop, loopPath]);

  /* ---------- Condition handlers ---------- */
  const updateCondition = (id, patch) => {
    setConditions((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const removeCondition = (id) => {
    setConditions((prev) => prev.filter((c) => c.id !== id));
  };

  const addCondition = () => {
    setConditions((prev) => [...prev, defaultConditionTemplate()]);
  };

  const handleCopy = () => {
    if (!code) return;
    // Avoid copying the "no rule configured" message
    if (code.startsWith("// No rule configured")) return;
    navigator.clipboard.writeText(code);
  };

  /* ---------- UI ---------- */
  return (
    <section className="card transformCard">
      <div className="cardHeader">
        <h2 className="titleWithBadge">
          Transformation Builder
          <span className="devBadge">Under Development</span>
        </h2>

        <div className="toolbar">
          {/* Language toggle */}
          <div className="segmented">
            <button
              className={`segBtn ${language === "velocity" ? "active" : ""}`}
              onClick={() => setLanguage("velocity")}
              type="button"
            >
              Velocity
            </button>
            <button
              className={`segBtn ${language === "python" ? "active" : ""}`}
              onClick={() => setLanguage("python")}
              type="button"
            >
              Python
            </button>
          </div>
        </div>
      </div>

      <div className="transformBody">
        {/* LEFT: Rule form */}
        <div className="formCol">
          {/* Loop */}
          

          {useLoop && (
            <div className="fieldGrid">
              <div className="field">
                <label className="label">Array Path</label>
                <select
                  className="select"
                  value={loopPath}
                  onChange={(e) => setLoopPath(e.target.value)}
                >
                  {arrayPaths.length === 0 && <option value="">(no array paths found)</option>}
                  {arrayPaths.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="label">Loop Alias</label>
                <input
                  className="input"
                  value={loopAlias}
                  onChange={(e) => setLoopAlias(e.target.value)}
                  placeholder="e.g. AsnLine"
                />
              </div>
            </div>
          )}

          {/* Conditions */}
          <div className="blockHeader">Conditions</div>
          {conditions.map((c, idx) => (
            <div className="conditionRow" key={c.id} style={{display: "flex", alignItems: "flex-start", flexWrap: "wrap", gap: 12}}>
<div className="condHeader" style={{display: "flex", alignItems: "center", gap: 8, marginRight: 8}}>
  <div style={{fontWeight: 600, minWidth: 28, textAlign: "center"}}>{idx + 1}.</div>
  <button
    type="button"
    className="iconBtn"
    title="Remove condition"
    onClick={() => removeCondition(c.id)}
    style={{ marginLeft: 4 }}
  >
    ✖
  </button>
</div>

              <select
                className="select condType"
                value={c.type}
                onChange={(e) => updateCondition(c.id, sanitizeConditionType(c, e.target.value))}
              >
                <option value="isNull">isNull(path)</option>
                <option value="notNull">notNull(path)</option>
                <option value="equals">equals(left, right)</option>
                <option value="notEquals">notEquals(left, right)</option>
                <option value="comparePaths">comparePaths(leftPath, op, rightPath)</option>
                <option value="exists">exists(path)</option>
                <option value="contains">contains(path, value)</option>
              </select>

              {(c.type === "isNull" || c.type === "notNull" || c.type === "exists") && (
                <select
                  className="select grow"
                  value={c.path || ""}
                  onChange={(e) => updateCondition(c.id, { path: e.target.value })}
                >
                  <option value="">Select path…</option>
                  {keyPaths.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              )}

              {(c.type === "equals" || c.type === "notEquals") && (
                <>
                  <select
                    className="select grow"
                    value={c.left || ""}
                    onChange={(e) => updateCondition(c.id, { left: e.target.value })}
                  >
                    <option value="">Left path…</option>
                    {keyPaths.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>

                  <div className="rightSide" style={{minWidth: "260px", flexShrink: 0}}>
                    
                    {c.rightIsPath ? (
                      <select
                        className="select grow"
                        value={c.right || ""}
                        onChange={(e) => updateCondition(c.id, { right: e.target.value })}
                      >
                        <option value="">Right path…</option>
                        {keyPaths.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="input grow"
                        placeholder='Right value (e.g. "CLOSED", 10, true, null)'
                        value={c.rightRaw !== undefined ? c.rightRaw : stringifyValue(c.right)}
                        onChange={(e) => updateCondition(c.id, { rightRaw: e.target.value })}
                      />
                    )}
                  </div>
                </>
              )}

              {c.type === "comparePaths" && (
                <>
                  <select
                    className="select grow"
                    value={c.left || ""}
                    onChange={(e) => updateCondition(c.id, { left: e.target.value })}
                  >
                    <option value="">Left path…</option>
                    {keyPaths.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  <select
                    className="select"
                    value={c.op || "=="}
                    onChange={(e) => updateCondition(c.id, { op: e.target.value })}
                  >
                    <option value="==">==</option>
                    <option value="!=">!=</option>
                    <option value=">">&gt;</option>
                    <option value="<">&lt;</option>
                    <option value=">=">&gt;=</option>
                    <option value="<=">&lt;=</option>
                  </select>
                  <select
                    className="select grow"
                    value={c.right || ""}
                    onChange={(e) => updateCondition(c.id, { right: e.target.value })}
                  >
                    <option value="">Right path…</option>
                    {keyPaths.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </>
              )}

              {c.type === "contains" && (
                <>
                  <select
                    className="select grow"
                    value={c.path || ""}
                    onChange={(e) => updateCondition(c.id, { path: e.target.value })}
                  >
                    <option value="">Array / string path…</option>
                    {keyPaths.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  <input
                    className="input grow"
                    placeholder='Value (e.g. "hot", 5, true, null)'
                    value={stringifyValue(c.value)}
                    onChange={(e) => updateCondition(c.id, { value: parseValue(e.target.value) })}
                  />
                </>
              )}

              
            </div>
          ))}

          <div className="fieldRow" style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button type="button" className="pillBtn" onClick={addCondition}>
              + Add Condition
            </button>

            <button type="button" className="pillBtn" onClick={resetBuilder}>
              Reset
            </button>
          </div>

          {/* Action */}
          <div className="blockHeader">Action</div>

          {/* Operation + Target path */}
          <div className="fieldGrid">
            <div className="field">
              <label className="label">Operation</label>
              <select
                className="select"
                value={action.op}
                onChange={(e) => setAction((a) => ({ ...a, op: e.target.value }))}
              >
                <option value="add">Add</option>
                <option value="update">Update</option>
                <option value="remove">Remove</option>
              </select>
            </div>

            <div className="field">
              <label className="label">
                {action.op === "add" ? "Parent Object Path" : "Target Path"}
              </label>
              <select
                className="select"
                value={action.path}
                onChange={(e) => setAction((a) => ({ ...a, path: e.target.value }))}
              >
                <option value="">Select path…</option>
                {keyPaths.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* New key (only when Add) */}
          {action.op === "add" && (
            <div className="fieldGrid">
              <div className="field">
                <label className="label">New Key</label>
                <input
                  className="input"
                  placeholder='e.g. isvalidAsn'
                  value={action.newKey}
                  onChange={(e) => setAction((a) => ({ ...a, newKey: e.target.value }))}
                />
              </div>

              <div className="field">
                <label className="label">Value</label>
                <input
                  className="input"
                  placeholder='e.g. true, 10, "BN-123", null, or $document.x.y'
                  value={stringifyValue(action.value)}
                  onChange={(e) => setAction((a) => ({ ...a, value: parseValue(e.target.value) }))}
                />
              </div>
            </div>
          )}

          {/* Value for Update */}
          {action.op === "update" && (
            <div className="fieldGrid">
              <div className="field">
                <label className="label">Value</label>
                <input
                  className="input"
                  placeholder='e.g. true, 10, "BN-123", null, or $document.x.y'
                  value={stringifyValue(action.value)}
                  onChange={(e) => setAction((a) => ({ ...a, value: parseValue(e.target.value) }))}
                />
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Code preview */}
        <div className="codeCol">
          <div className="codeHeader">
            <div className="hint">
              Generated {language === "velocity" ? "Velocity" : "Python"} Code
            </div>
            <button
              className="pillBtn"
              onClick={handleCopy}
              type="button"
              disabled={code.startsWith("// No rule configured")}
            >
              Copy Snippet
            </button>
          </div>
          <pre className="codeBlock" aria-label="Generated code">
{code || "// Configure a rule on the left to generate code here…"}
          </pre>
        </div>
      </div>
    </section>
  );
};

export default TransformBuilder;

/* ---------- Helpers used by UI normalization ---------- */

function sanitizeConditionType(existing, nextType) {
  const base = { type: nextType };
  switch (nextType) {
    case "isNull":
    case "notNull":
    case "exists":
      return { ...base, path: "" };
    case "equals":
    case "notEquals":
      return { ...base, left: "", right: "", rightRaw: "", rightIsPath: false };
    case "comparePaths":
      return { ...base, left: "", op: "==", right: "" };
    case "contains":
      return { ...base, path: "", value: "" };
    default:
      return base;
  }
}

/**
 * normalizeRule(rule)
 * - parse rightRaw into right using parseValue
 * - drop incomplete conditions
 * - return the normalized rule consumed by codegen
 */
function normalizeRule(rule) {
  // parse and sanitize conditions, but drop incomplete ones
  const cleanConds = (rule.conditions || [])
    .filter((c) => c && c.type)
    .map((c) => {
      const copy = { ...c };

      // If user provided raw input, parse it into a typed JS value (number/string/null/boolean/path)
      if (!copy.rightIsPath && typeof copy.rightRaw === "string" && copy.rightRaw.trim() !== "") {
        copy.right = parseValue(copy.rightRaw);
      }

      // Ensure path-right paths are valid
      if (copy.rightIsPath && typeof copy.right === "string" && !copy.right.startsWith("$document")) {
        copy.right = "";
      }

      // Remove helper raw field before sending to codegen
      delete copy.rightRaw;

      return copy;
    })
    // now filter out incomplete conditions depending on type
    .filter((c) => {
      switch ((c.type || "").toLowerCase()) {
        case "isnull":
        case "notnull":
        case "exists":
        case "contains":
          return !!c.path; // require a path
        case "equals":
        case "notequals":
          return !!c.left && (c.rightIsPath ? !!c.right : (c.right !== "" && c.right !== undefined));
        case "comparepaths":
          return !!c.left && !!c.right;
        default:
          return false;
      }
    });

  const normalized = {
    language: rule.language || "velocity",
    conditions: cleanConds,
    action: rule.action || {},
  };

  if (rule.loop && rule.loop.path) {
    normalized.loop = { ...rule.loop };
  }

  return normalized;
}
